import i18n from "../../../utils/translations/i18n";
import { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { signinFormLogic } from "./formLogic";
import Styles from "./index.module.scss";
import Partition from "../../shared/elements/partition";
import SocialLoginButtons from "../../shared/elements/socialLoginButton";
import AuthForm from "../../shared/sections/auth-form";
import SuccessBar from "../../shared/elements/successBar";
import { useAuthFrontLock } from '../../../context/useAuthFrontContext';
import { useScreen } from '../../../context/useScreen';
import { useLoader } from '../../../context/useLoader';
import ErrorBar from "../../shared/elements/errorBar";
import { PAGE_NAME, ERROR_CODE, COOKIE_CONSTANT, SUCCESS_MESSAGES, EXCLUDES_MESSAGES } from "../../../utils/constant/constant";
import { deleteCookie, redirectToAuthcallback, setCurrentPageCookie } from "../../../utils/utils/utils";

const SignIn = () => {
    
    const { t } = i18n;
    const location = useLocation();
    const successBarState = location.state?.successBarState || false;
    const [form, setFormState] = useState(JSON.parse(JSON.stringify(signinFormLogic)));
    const [showSuccessBar, setShowSuccessBar] = useState(successBarState);
    const [errorBarState, setErrorBarState] = useState({isActive:false, errorType: '', message: ''});
    const [modelMessage,setmodelMessage]=useState(t('sign_in.password_updated'));
    const authFrontLock = useAuthFrontLock();
    const screen = useScreen();
    const { showLoader, hideLoader } = useLoader();

    const errorMessages = {
        [ERROR_CODE.NULL_ERROR]: t('sign_in.mandatory_field_error'),
        [ERROR_CODE.INVALID_CREDENTIALS]: t('sign_in.invalid_credentials_error'),
        [ERROR_CODE.BRUTE_FORCE_PROTECTION]: t('sign_in.max_attempt_error'),
        [ERROR_CODE.GENERIC_ERROR]: t('sign_in.generic_error'),
        [ERROR_CODE.SOCIAL_SIGNIN_ERROR]: t('sign_in.social_signin_error'),
        [ERROR_CODE.EMAIL_UNVERIFIED]: t('sign_in.account_unverified_error'),
        [ERROR_CODE.POST_CHECKOUT_GUEST_USER_SIGNUP]: t('sign_in.guest_user_signin_error'),
        [ERROR_CODE.EMAIL_NOT_PRESENT]: t('sign_in.social_signin_email_not_present_error')
    };

    useEffect(() => {
        document.title = t('sign_in.page_title');

        const { screenId, errorId, error, errorDescription } = authFrontLock.getAdditionalParams();
        const errorBarId = window.localStorage.getItem('errorBarId');

        if (error && errorId && errorBarId !== errorId.toString()) {
            window.localStorage.setItem('errorBarId', errorId);
            const loginHint = authFrontLock.getLoginHint();
            if (loginHint) {
                setFormState(prevState => ({
                    ...prevState,
                    email: {
                        ...prevState.email,
                        value: loginHint
                    }
                }));
            }
        
            showCallbackError(error, errorDescription);  
        }

        setCurrentPageCookie( PAGE_NAME.SIGN_IN, screenId );
    }, []);

    const showCallbackError = (error: string, errorDescription: string) => {
        if (EXCLUDES_MESSAGES.includes(error)) {
            return;
        } 

        if (SUCCESS_MESSAGES.includes(error)) {
            if (error == ERROR_CODE.EMAIL_VERIFIED) {
                const urlParams = new URLSearchParams(window.location.search);
                const activationParam = urlParams.get('success');
                if (activationParam == "true") {
                    setmodelMessage(t('activate_user.verification_successful.message'));
                } else {
                    setmodelMessage(t('activate_user.already_verified.message'));
                }
            }
            setShowSuccessBar(true);
        } else {
            let errorCode;
            if (error === ERROR_CODE.EMAIL_UNVERIFIED) {
                errorCode = ERROR_CODE.EMAIL_UNVERIFIED;
            } else if (error === ERROR_CODE.ACCESS_DENIED && errorDescription === ERROR_CODE.SOCIAL_SIGN_IN_FAILURE) {
                errorCode = ERROR_CODE.SOCIAL_SIGNIN_ERROR;
            }else if(error === ERROR_CODE.ACCESS_DENIED && (errorDescription === ERROR_CODE.MISSING_EMAIL || errorDescription === ERROR_CODE.INVALID_EMAIL_ADDRESS)){
                errorCode = ERROR_CODE.EMAIL_NOT_PRESENT;
            }else if (error === ERROR_CODE.POST_CHECKOUT_GUEST_USER_SIGNUP) {
                errorCode = ERROR_CODE.POST_CHECKOUT_GUEST_USER_SIGNUP;
            } else {
                errorCode = ERROR_CODE.GENERIC_ERROR;
            }
            showErrorMessage(errorCode);
        }
    }

    const hideSuccessBar = () => {
        if(showSuccessBar) {
            setShowSuccessBar(false);
        }
    }

    const signUp = (event) => {
        event.preventDefault();
        hideSuccessBar();
        screen.setDisplayScreen(PAGE_NAME.SIGN_UP);
    }

    const resetFormFieldsErrors = () => {
        const updatedForm = { ...form };
        updatedForm.formFields.forEach(field => {
            updatedForm[field].dangerWarn = false;
            updatedForm[field].errorMessage = null;
        });
        setFormState(updatedForm);
    };

    const showErrorMessage = (error, email?) => {  
        if(error === ERROR_CODE.REAUTHENTICATION_REQUIRED){
            deleteCookie(COOKIE_CONSTANT.CURRENT_PAGE_COOKIE_KEY);
            authFrontLock && redirectToAuthcallback(authFrontLock, ERROR_CODE.REAUTHENTICATION_REQUIRED, email)
            return;
        }
        if(error === ERROR_CODE.SOCIAL_SIGNIN_ERROR){
            resetFormFieldsErrors();
        }

        if (errorMessages[error]) {
            setErrorBarState({ isActive: true, errorType: error, message: errorMessages[error] });
        } else {
            setErrorBarState({ isActive: true, errorType: ERROR_CODE.GENERIC_ERROR, message: errorMessages[ERROR_CODE.GENERIC_ERROR] });
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormState(prevForm => ({
            ...prevForm,
            [name]: {
                ...prevForm[name],
                value,
                dangerWarn: false,
                errorMessage: null
            }
        }));
    };

    const handleBlur = (event) => {
        const fieldName = event.target.name;
        const { regex, value } = form[fieldName];
        const expression = new RegExp(regex);

        const showRegexError = value !== '' && !expression.test(value);
        const showNullError = value === '' && errorBarState.isActive && errorBarState.errorType === ERROR_CODE.NULL_ERROR;

        setFormState(prevForm => ({
            ...prevForm,
            [fieldName]: {
                ...prevForm[fieldName],
                dangerWarn: showRegexError || showNullError,
                errorMessage: showRegexError ? t('sign_in.invalid_email_error') : null
            }
        }));
    };

    const validateForm = () => {
        const formObj = { ...form };
        let regexError = false, nullError = false;

        formObj.formFields.forEach(field => {
            const { regex, value, required } = formObj[field];
            const expression = new RegExp(regex);
            formObj[field].dangerWarn = false;
            formObj[field].errorMessage = '';

            if (value !== '' && !expression.test(value)) {
                regexError = true;
                formObj[field].dangerWarn = true;
                formObj[field].errorMessage = t('sign_in.invalid_email_error');
            }

            if (value === '' && required) {
                nullError = true;
                formObj[field].dangerWarn = true;
            }
        });

        if (nullError) {
            showErrorMessage(ERROR_CODE.NULL_ERROR);
        } else {
            setErrorBarState({ isActive: false, errorType: '', message: '' });
        }

        setFormState(formObj);
        return !(regexError || nullError);
    };

    const handleSubmit = async (event) => {
        try {
            event.preventDefault();
            hideSuccessBar();
            if (validateForm()) {
                showLoader();
                const email = form.email.value;
                const password = form.password.value;
                await authFrontLock.loginAsync({ email: email, password: password, captcha : authFrontLock.getCaptchaToken() });
            }
          } catch (err : any) {
            showErrorMessage(err.errorCode, form.email.value);
            hideLoader();
          }
      
    }

    const toggleShowPassword = (fieldName) => {
        const showPassword = !form[fieldName].showPassword;
        setFormState(prevForm => ({
            ...prevForm,
            [fieldName]: {
                ...prevForm[fieldName],
                showPassword,
                type: showPassword ? 'text' : 'password'
            }
        }));
    };

    return (
        <>
            <div className={`${Styles.container} option-a`}>
                <div className={Styles.header}>
                    <h2 className={Styles.title}>{t('sign_in.title')}</h2>
                    <div>
                        <span className={Styles.subtle}>{t('sign_in.or')}&nbsp;</span>
                        <span onClick={signUp} className={Styles.boldlink} role="button" tabIndex={0}> {t('sign_up.create_an_account')}</span>
                    </div>
                </div>
                {showSuccessBar && <SuccessBar message={modelMessage} />}
                {errorBarState.isActive && <ErrorBar message={errorBarState.message} />}    
                <AuthForm isSignupForm={false} form={form} handleClick={hideSuccessBar} handleChange={handleChange} handleBlur={handleBlur} handleSubmit={handleSubmit} showPasswordHandler={toggleShowPassword} option='option-a'/>
                <div className={Styles.footer}>
                    <Partition breakPartition={false}/>
                    <div className={Styles.footerTitle}>
                        {t('sign_in.continue_with')}
                    </div>
                    <SocialLoginButtons showErrorMessage={showErrorMessage}/>
                </div>
            </div>
            <div className={`${Styles.container} option-b`}>
                <div className={`${Styles.header} ${Styles.additionalGap}`}>
                <h2 className={Styles.title}>{t('sign_in.title')}</h2>
                    {errorBarState.isActive && errorBarState.errorType===ERROR_CODE.SOCIAL_SIGNIN_ERROR && <ErrorBar message={errorBarState.message} />}    
                    <SocialLoginButtons showErrorMessage={showErrorMessage}/>
                </div>
                <Partition breakPartition={true} text={t('sign_in.continue_with_email')}/>
                {showSuccessBar && <SuccessBar message={modelMessage} />}
                {errorBarState.isActive && errorBarState.errorType!==ERROR_CODE.SOCIAL_SIGNIN_ERROR && <ErrorBar message={errorBarState.message} />}    
                <AuthForm isSignupForm={false} form={form} handleClick={hideSuccessBar} handleChange={handleChange} handleBlur={handleBlur} handleSubmit={handleSubmit} showPasswordHandler={toggleShowPassword} option='option-b'/>
                <div className={`${Styles.footer} ${Styles.additionalGap}`}>
                    <Partition breakPartition={false}/>
                    <div>
                        <span className={Styles.subtle}>{t('sign_in.or')}&nbsp;</span>
                        <span onClick={signUp} className={Styles.boldlink} role="button" tabIndex={0}> {t('sign_up.create_an_account')}</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignIn;